import { useState, useCallback, useEffect } from 'react'
import { useTranslation } from '../../contexts/LanguageContext'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { SortableList, SortableItem, DragHandle } from '../ui/SortableList'
import { CVLabelInput } from './CVLabelInput'
import type { Education } from '../../types/cv'

interface EducationFormProps {
  data: Education[] | undefined
  onAdd: () => void
  onUpdate: (id: string, data: Partial<Education>) => void
  onRemove: (id: string) => void
  onReorder?: (oldIndex: number, newIndex: number) => void
  customLabel?: string
  onCustomLabelChange: (value: string) => void
}

export function EducationForm({
  data,
  onAdd,
  onUpdate,
  onRemove,
  onReorder,
  customLabel,
  onCustomLabelChange,
}: EducationFormProps) {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)

  // Open newly added education
  useEffect(() => {
    if (data && data.length > 0) {
      const lastEdu = data[data.length - 1]
      if (!lastEdu.institution && !lastEdu.degree) {
        setOpenId(lastEdu.id)
      }
    }
  }, [data?.length])

  const handleChange = useCallback(
    (id: string, field: keyof Education) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onUpdate(id, { [field]: e.target.value })
      },
    [onUpdate]
  )

  const handleCurrentChange = useCallback(
    (id: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate(id, {
        current: e.target.checked,
        endDate: e.target.checked ? undefined : '',
      })
    },
    [onUpdate]
  )

  const toggleExpand = (id: string) => {
    setOpenId(openId === id ? null : id)
  }

  const getTitle = (edu: Education, index: number) => {
    if (edu.degree || edu.institution) {
      const parts = [edu.degree, edu.institution].filter(Boolean)
      return parts.join(' - ') || `${t.educationForm.itemTitle} ${index + 1}`
    }
    return `${t.educationForm.itemTitle} ${index + 1}`
  }

  return (
    <div className="space-y-2">
      <CVLabelInput value={customLabel} placeholder="Education" onChange={onCustomLabelChange} />
      {(!data || data.length === 0) && (
        <p className="text-sm text-slate-500 italic">
          {t.educationForm.empty}
        </p>
      )}

      <SortableList
        items={(data || []).map(edu => edu.id)}
        onReorder={onReorder || (() => {})}
      >
        {(data || []).map((edu, index) => {
          const isOpen = openId === edu.id
          return (
            <SortableItem key={edu.id} id={edu.id}>
              {({ dragHandleProps, isDragging }) => (
                <div
                  className={`border border-slate-200 rounded-lg overflow-hidden ${isDragging ? 'shadow-lg' : ''}`}
                >
                  {/* Header */}
                  <div
                    className={`flex items-center p-3 transition-colors ${
                      isOpen ? 'bg-slate-50' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    {onReorder && <DragHandle {...dragHandleProps} />}
                    <button
                      type="button"
                      onClick={() => toggleExpand(edu.id)}
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          edu.institution || edu.degree ? 'bg-green-500' : 'bg-slate-300'
                        }`} />
                        <span className="font-medium text-slate-900 truncate">
                          {getTitle(edu, index)}
                        </span>
                      </div>
                      <svg
                        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  {isOpen && (
                    <div className="p-4 border-t border-slate-200 bg-white space-y-4">
                      <Input
                        label={t.educationForm.institution}
                        placeholder={t.educationForm.institutionPlaceholder}
                        value={edu.institution || ''}
                        onChange={handleChange(edu.id, 'institution')}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label={t.educationForm.degree}
                          placeholder={t.educationForm.degreePlaceholder}
                          value={edu.degree || ''}
                          onChange={handleChange(edu.id, 'degree')}
                        />
                        <Input
                          label={t.educationForm.field}
                          placeholder={t.educationForm.fieldPlaceholder}
                          value={edu.field || ''}
                          onChange={handleChange(edu.id, 'field')}
                        />
                      </div>

                      <Input
                        label={t.educationForm.location}
                        placeholder={t.educationForm.locationPlaceholder}
                        value={edu.location || ''}
                        onChange={handleChange(edu.id, 'location')}
                      />

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label={t.educationForm.startDate}
                          type="month"
                          value={edu.startDate || ''}
                          onChange={handleChange(edu.id, 'startDate')}
                        />
                        <div>
                          <Input
                            label={t.educationForm.endDate}
                            type="month"
                            value={edu.endDate || ''}
                            onChange={handleChange(edu.id, 'endDate')}
                            disabled={edu.current}
                          />
                          <label className="flex items-center gap-2 mt-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={edu.current || false}
                              onChange={handleCurrentChange(edu.id)}
                              className="w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary"
                            />
                            <span className="text-sm text-slate-600">
                              {t.educationForm.currentlyStudying}
                            </span>
                          </label>
                        </div>
                      </div>

                      <Input
                        label={t.educationForm.gpa}
                        placeholder={t.educationForm.gpaPlaceholder}
                        value={edu.gpa || ''}
                        onChange={handleChange(edu.id, 'gpa')}
                      />

                      <div className="pt-2 border-t border-slate-100">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemove(edu.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          {t.educationForm.delete}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </SortableItem>
          )
        })}
      </SortableList>

      <Button type="button" variant="secondary" onClick={onAdd} className="w-full">
        {t.educationForm.add}
      </Button>
    </div>
  )
}
